import { useRef, useState, useCallback, useEffect, type ReactNode, type PointerEvent } from 'react'

export type SheetPosition = 'minimized' | 'half' | 'maximized'

interface DraggableSheetProps {
  children: ReactNode
  position: SheetPosition
  onPositionChange: (position: SheetPosition) => void
  headerContent?: ReactNode
  /** Available height (viewport minus fixed headers) */
  availableHeight: number
}

/** Snap point heights as percentages of available height */
const SNAP_POINTS: Record<SheetPosition, number> = {
  minimized: 0.15, // ~100px on most screens
  half: 0.55,      // More list visible by default
  maximized: 0.90,
}

/** Minimum height in pixels for minimized state */
const MIN_HEIGHT_PX = 80

/**
 * Calculate the actual height for a given position
 */
function getHeightForPosition(position: SheetPosition, availableHeight: number): number {
  const percentage = SNAP_POINTS[position]
  const calculatedHeight = availableHeight * percentage
  // Ensure minimized state has at least MIN_HEIGHT_PX
  if (position === 'minimized') {
    return Math.max(calculatedHeight, MIN_HEIGHT_PX)
  }
  return calculatedHeight
}

/**
 * Get the closest snap position based on current height
 */
function getClosestSnapPosition(currentHeight: number, availableHeight: number): SheetPosition {
  const positions: SheetPosition[] = ['minimized', 'half', 'maximized']
  let closest: SheetPosition = 'half'
  let minDistance = Infinity

  for (const pos of positions) {
    const targetHeight = getHeightForPosition(pos, availableHeight)
    const distance = Math.abs(currentHeight - targetHeight)
    if (distance < minDistance) {
      minDistance = distance
      closest = pos
    }
  }

  return closest
}

/**
 * Draggable bottom sheet with snap points for minimized, half, and maximized positions.
 * Supports both touch and mouse/pointer interactions.
 */
export function DraggableSheet({
  children,
  position,
  onPositionChange,
  headerContent,
  availableHeight,
}: DraggableSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [currentHeight, setCurrentHeight] = useState(() =>
    getHeightForPosition(position, availableHeight)
  )
  const dragStartY = useRef(0)
  const dragStartHeight = useRef(0)

  // Update height when position changes externally or available height changes
  useEffect(() => {
    if (!isDragging) {
      setCurrentHeight(getHeightForPosition(position, availableHeight))
    }
  }, [position, availableHeight, isDragging])

  const handlePointerDown = useCallback((e: PointerEvent<HTMLDivElement>) => {
    // Only handle primary button (left click / touch)
    if (e.button !== 0) return

    // Don't start drag if clicking on interactive elements (buttons, inputs, etc.)
    const target = e.target as HTMLElement
    if (target.closest('button, a, input, select, [role="button"]')) {
      return
    }

    // Capture pointer for tracking outside element
    e.currentTarget.setPointerCapture(e.pointerId)

    setIsDragging(true)
    dragStartY.current = e.clientY
    dragStartHeight.current = currentHeight
  }, [currentHeight])

  const handlePointerMove = useCallback((e: PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return

    const deltaY = dragStartY.current - e.clientY
    const newHeight = Math.max(
      MIN_HEIGHT_PX,
      Math.min(availableHeight * SNAP_POINTS.maximized, dragStartHeight.current + deltaY)
    )
    setCurrentHeight(newHeight)
  }, [isDragging, availableHeight])

  const handlePointerUp = useCallback((e: PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return

    e.currentTarget.releasePointerCapture(e.pointerId)
    setIsDragging(false)

    // Determine velocity direction for better UX
    const deltaY = dragStartY.current - e.clientY
    const velocity = deltaY / 100 // Simplified velocity

    // If significant velocity, bias toward that direction
    let targetPosition: SheetPosition
    if (Math.abs(velocity) > 0.5) {
      if (velocity > 0) {
        // Swiping up - go to next higher position
        if (position === 'minimized') targetPosition = 'half'
        else if (position === 'half') targetPosition = 'maximized'
        else targetPosition = 'maximized'
      } else {
        // Swiping down - go to next lower position
        if (position === 'maximized') targetPosition = 'half'
        else if (position === 'half') targetPosition = 'minimized'
        else targetPosition = 'minimized'
      }
    } else {
      // Low velocity - snap to closest
      targetPosition = getClosestSnapPosition(currentHeight, availableHeight)
    }

    onPositionChange(targetPosition)
    setCurrentHeight(getHeightForPosition(targetPosition, availableHeight))
  }, [isDragging, currentHeight, availableHeight, position, onPositionChange])

  const handlePointerCancel = useCallback((e: PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return
    e.currentTarget.releasePointerCapture(e.pointerId)
    setIsDragging(false)
    // Return to current position
    setCurrentHeight(getHeightForPosition(position, availableHeight))
  }, [isDragging, position, availableHeight])

  return (
    <div
      ref={sheetRef}
      className={`draggable-sheet ${isDragging ? 'dragging' : ''}`}
      style={{
        height: currentHeight,
        transition: isDragging ? 'none' : 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {/* Drag handle */}
      <div
        className="draggable-sheet-handle"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        style={{ touchAction: 'none' }}
      >
        <div className="draggable-sheet-handle-bar" />
        {headerContent && (
          <div className="draggable-sheet-header">
            {headerContent}
          </div>
        )}
      </div>

      {/* Scrollable content */}
      <div className="draggable-sheet-content">
        {children}
      </div>
    </div>
  )
}
